import React, { useState, useEffect, useRef } from 'react';
import { Navbar, Container, Nav } from 'react-bootstrap';
import styled, { keyframes } from 'styled-components';
import { FiEdit3 } from 'react-icons/fi';

const breathingGlow = keyframes`
  0%   { box-shadow: 0 0 10px rgba(160,68,255,0.2), inset 0 0 5px rgba(160,68,255,0.1); border-color: rgba(160,68,255,0.3); }
  50%  { box-shadow: 0 0 25px rgba(160,68,255,0.5), inset 0 0 10px rgba(160,68,255,0.2); border-color: rgba(160,68,255,0.8); }
  100% { box-shadow: 0 0 10px rgba(160,68,255,0.2), inset 0 0 5px rgba(160,68,255,0.1); border-color: rgba(160,68,255,0.3); }
`;

const StyledNavbar = styled(Navbar)`
  transition: all 0.3s ease-out;
  background: ${({ scrolled }) => (scrolled === 'true' ? 'rgba(1,2,8,0.85)' : 'transparent')};
  backdrop-filter: ${({ scrolled }) => (scrolled === 'true' ? 'blur(16px)' : 'none')};
  -webkit-backdrop-filter: ${({ scrolled }) => (scrolled === 'true' ? 'blur(16px)' : 'none')};
  border-bottom: ${({ scrolled }) => (scrolled === 'true' ? '1px solid rgba(160,68,255,0.15)' : 'none')};
  z-index: 2000;

  .navbar-brand, .nav-link {
    color: #fff !important;
    font-weight: 600;
    transition: color 0.2s ease;
    text-shadow: 0 1px 3px rgba(0,0,0,0.3);
  }

  .nav-link:hover { color: #a044ff !important; }

  .apply-btn {
    position: relative;
    background: rgba(160,68,255,0.08) !important;
    color: rgba(255,255,255,0.9) !important;
    padding: 0.6rem 1.8rem !important;
    border-radius: 999px !important;
    font-weight: 900 !important;
    margin-left: 1.5rem !important;
    border: 1.5px solid rgba(160,68,255,0.35) !important;
    transition: all 0.3s cubic-bezier(0.4,0,0.2,1) !important;
    font-size: 0.9rem !important;
    letter-spacing: 0.05em !important;
    display: flex !important;
    align-items: center;
    gap: 0.6rem;
    animation: ${breathingGlow} 3s infinite ease-in-out;

    svg { transition: all 0.3s ease; color: rgba(160,68,255,0.8); }

    &:hover {
      transform: translateY(-2px);
      background: rgba(160,68,255,0.18) !important;
      border-color: #a044ff !important;
      color: #fff !important;
      box-shadow: 0 8px 25px rgba(160,68,255,0.3);
      animation: none;
      svg { transform: scale(1.1); color: #a044ff; }
    }
  }

  .navbar-brand { display: flex; align-items: center; font-weight: 700; }
  .navbar-brand img { height: 18px; margin-right: 10px; }

  @media (max-width: 991.98px) {
    .navbar-collapse {
      position: fixed;
      top: 0; left: 0;
      width: 100%; height: 100vh;
      background: rgba(1,2,8,0.97);
      backdrop-filter: blur(20px);
      -webkit-backdrop-filter: blur(20px);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      padding: 2rem;
      transition: all 0.4s cubic-bezier(0.4,0,0.2,1);
      border: none; margin: 0;
      opacity: 0; visibility: hidden; transform: translateY(-20px);
    }
    .navbar-collapse.show { opacity: 1; visibility: visible; transform: translateY(0); }
    .navbar-nav { width: 100%; gap: 1.5rem; align-items: center; }
    .nav-link {
      font-size: 1.5rem !important;
      font-weight: 700 !important;
      color: #fff !important;
      padding: 1rem !important;
      border: none !important;
      opacity: 0.85;
      transition: all 0.3s ease;
      display: flex; flex-direction: column; align-items: center; gap: 0.2rem;
      width: auto !important;
      &::after { content: attr(data-label); font-size: 0.8rem; font-weight: 400; color: #a044ff; opacity: 0.7; }
      &:active { transform: scale(0.95); color: #a044ff !important; }
    }
    .apply-btn {
      margin-left: 0 !important; margin-top: 2rem !important;
      width: 100% !important; max-width: 280px;
      font-size: 1.1rem !important; padding: 1rem 2rem !important;
      justify-content: center !important;
    }
  }
`;

const CloseButton = styled.button`
  display: none;
  @media (max-width: 991.98px) {
    display: block; position: absolute; top: 2rem; right: 2rem;
    background: none; border: none; color: #fff; font-size: 2rem; cursor: pointer; z-index: 2100;
  }
`;

const StyledToggle = styled(Navbar.Toggle)`
  border: 1px solid rgba(160,68,255,0.3) !important;
  border-radius: 10px;
  background: rgba(160,68,255,0.05) !important;
  transition: all 0.3s ease;
  padding: 0.5rem 0.75rem;
  &:hover, &:focus {
    background: rgba(160,68,255,0.12) !important;
    border-color: #a044ff !important;
    box-shadow: 0 0 15px rgba(160,68,255,0.3);
  }
  .navbar-toggler-icon {
    width: 1.5em; height: 1.5em;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 30 30'%3e%3cpath stroke='rgba(255,255,255,0.9)' stroke-linecap='round' stroke-miterlimit='10' stroke-width='2' d='M4 7h22M4 15h22M4 23h22'/%3e%3c/svg%3e") !important;
  }
`;

const FloatingNav = styled.div`
  position: fixed;
  right: 1.5rem; top: 50%;
  transform: translateY(-50%);
  display: flex; flex-direction: column; gap: 1.2rem;
  z-index: 1000; pointer-events: none;
  @media (max-width: 991.98px) { right: 0.5rem; gap: 2rem; }
`;

const NavDot = styled.a`
  pointer-events: auto;
  display: flex; align-items: center; justify-content: flex-end;
  gap: 1rem; text-decoration: none; padding: 5px;
  @media (max-width: 991.98px) { padding: 10px; }

  .label {
    opacity: 0; transform: translateX(10px);
    transition: all 0.4s cubic-bezier(0.4,0,0.2,1);
    background: rgba(160,68,255,0.25); backdrop-filter: blur(15px);
    border: 1px solid rgba(160,68,255,0.4);
    padding: 0.55rem 1.2rem 0.45rem; border-radius: 10px;
    color: #fff; font-size: 0.85rem; font-weight: 700; white-space: nowrap;
    box-shadow: 0 8px 25px rgba(0,0,0,0.4); pointer-events: none;
    display: flex; align-items: center; justify-content: center; line-height: 1;
    &.show-temp { opacity: 1; transform: translateX(0); }
    @media (max-width: 991.98px) { padding: 0.6rem 1rem 0.4rem; }
  }

  .dot {
    width: 12px; height: 12px;
    background: rgba(255,255,255,0.2);
    border: 2px solid rgba(255,255,255,0.1);
    border-radius: 50%;
    transition: all 0.3s cubic-bezier(0.175,0.885,0.32,1.275);
    @media (max-width: 991.98px) { width: 10px; height: 10px; }
  }

  &:hover .dot {
    background: #a044ff; border-color: rgba(160,68,255,0.5);
    transform: scale(1.3); box-shadow: 0 0 15px #a044ff;
  }
`;

// 성인반 페이지 섹션 정의
const navItems = [
  { id: 'adult-schedule', label: '개강 일정',  subLabel: 'Schedule' },
  { id: 'adult-program',  label: '수업 구성',  subLabel: 'Program' },
  { id: 'adult-example',  label: '실습 예제',  subLabel: 'Example' },
  { id: 'adult-roadmap',  label: '커리큘럼',   subLabel: 'Curriculum' },
  { id: 'adult-info',     label: '수강 정보',  subLabel: 'Info' },
];

const MAIN_URL = 'https://narnia-lab.com';

const AdultHeader = () => {
  const [scrolled, setScrolled]         = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [tempShowLabel, setTempShowLabel] = useState(null);
  const [expanded, setExpanded]         = useState(false);
  const labelTimerRef = useRef(null);

  const showLabelTemporarily = (id) => {
    setTempShowLabel(id);
    if (labelTimerRef.current) clearTimeout(labelTimerRef.current);
    labelTimerRef.current = setTimeout(() => setTempShowLabel(null), 3000);
  };

  useEffect(() => {
    if (activeSection && activeSection !== 'hero') showLabelTemporarily(activeSection);
    return () => { if (labelTimerRef.current) clearTimeout(labelTimerRef.current); };
  }, [activeSection]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      const threshold = 250;
      let current = 'hero';
      for (const item of navItems) {
        const el = document.getElementById(item.id);
        if (el && el.getBoundingClientRect().top <= threshold) current = item.id;
      }
      if (current !== activeSection) setActiveSection(current);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeSection]);

  const scrollTo = (id) => {
    setExpanded(false);
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.pageYOffset - 100;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  return (
    <>
      <StyledNavbar
        scrolled={scrolled.toString()}
        expand="lg"
        fixed="top"
        expanded={expanded}
        onToggle={(val) => setExpanded(val)}
      >
        <Container>
          <Navbar.Brand href={MAIN_URL} onClick={() => setExpanded(false)}>
            <img src={`${process.env.PUBLIC_URL}/naniaraeb_white.png`} alt="NARNIA LAB" />
          </Navbar.Brand>
          <StyledToggle aria-controls="adult-navbar-nav" />
          <Navbar.Collapse id="adult-navbar-nav">
            <CloseButton onClick={() => setExpanded(false)}>×</CloseButton>
            <Nav className="ms-auto align-items-center">
              {/* 모바일 메뉴 */}
              <div className="d-lg-none w-100 text-center">
                {navItems.map((item) => (
                  <Nav.Link
                    key={item.id}
                    onClick={() => scrollTo(item.id)}
                    data-label={item.subLabel}
                    style={{
                      color: activeSection === item.id ? '#a044ff' : '#fff',
                      opacity: activeSection === item.id ? 1 : 0.7,
                    }}
                  >
                    {item.label}
                  </Nav.Link>
                ))}
              </div>

              {/* 데스크톱 메뉴 */}
              {navItems.map((item) => (
                <Nav.Link
                  key={item.id}
                  className="d-none d-lg-block"
                  onClick={() => scrollTo(item.id)}
                  style={{
                    color: activeSection === item.id ? '#a044ff' : 'inherit',
                    cursor: 'pointer',
                  }}
                >
                  {item.label}
                </Nav.Link>
              ))}

              {/* 신청 버튼 */}
              <Nav.Link
                className="apply-btn"
                onClick={() => { setExpanded(false); window.location.href = '/apply#public-event-apply'; }}
              >
                <FiEdit3 size={18} />
                <span>신청하기</span>
              </Nav.Link>

              {/* 청소년 과정으로 이동 */}
              <Nav.Link
                className="d-none d-lg-block"
                href="/apply"
                style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.82rem', marginLeft: '0.5rem' }}
              >
                청소년 과정 →
              </Nav.Link>
            </Nav>
          </Navbar.Collapse>
        </Container>
      </StyledNavbar>

      {/* 우측 도트 네비게이션 */}
      <FloatingNav>
        {navItems.map((item) => (
          <NavDot
            key={item.id}
            href={`#${item.id}`}
            onClick={(e) => { e.preventDefault(); scrollTo(item.id); if (window.innerWidth > 991.98) showLabelTemporarily(item.id); }}
            onMouseEnter={() => { if (window.innerWidth > 991.98) showLabelTemporarily(item.id); }}
          >
            <span className={`label ${tempShowLabel === item.id ? 'show-temp' : ''}`}>{item.label}</span>
            <div className="dot" style={{
              background:   activeSection === item.id ? '#a044ff' : 'rgba(255,255,255,0.2)',
              transform:    activeSection === item.id ? 'scale(1.3)' : 'scale(1)',
              borderColor:  activeSection === item.id ? 'rgba(160,68,255,0.5)' : 'rgba(255,255,255,0.1)',
              boxShadow:    activeSection === item.id ? '0 0 15px #a044ff' : 'none',
            }} />
          </NavDot>
        ))}
      </FloatingNav>
    </>
  );
};

export default AdultHeader;
